import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Platform,
  Modal,
  StyleSheet,
  SafeAreaView,
  FlatList,
} from "react-native";
import DateTimePicker from "@react-native-community/datetimepicker";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { FontFamily } from "../../constants/fonts";
import { useTheme } from "../../theme";

interface MobileDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  displayText?: string;
  highlightedRange?: { start: Date; end: Date } | null;
  error?: boolean;
  disabled?: boolean;
  iconColor?: string;
  disabledIconColor?: string;
}
const formatDate = (date: Date | null): string =>
  date?.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }) || "";

const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const clampDate = (
  date: Date,
  minimumDate?: Date,
  maximumDate?: Date
): Date => {
  const normalizedDate = normalizeDate(date);
  const normalizedMinimum = minimumDate ? normalizeDate(minimumDate) : undefined;
  const normalizedMaximum = maximumDate ? normalizeDate(maximumDate) : undefined;

  if (normalizedMinimum && normalizedDate < normalizedMinimum) {
    return normalizedMinimum;
  }

  if (normalizedMaximum && normalizedDate > normalizedMaximum) {
    return normalizedMaximum;
  }

  return normalizedDate;
};

// Create custom iOS date picker components
const CustomIOSDatePicker = ({
  value,
  onChange,
  minimumDate,
  maximumDate,
  highlightedRange,
}: {
  value: Date;
  onChange: (date: Date) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  highlightedRange?: { start: Date; end: Date } | null;
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // Dark-mode only overrides: grey surface instead of pure black (light mode untouched)
  const dkContainer = isDark && { backgroundColor: colors.background.elevated };
  const dkSelected = isDark && { backgroundColor: colors.input.border };
  const today = new Date();

  // Initialize year, month, day states
  const boundedInitialDate = clampDate(value, minimumDate, maximumDate);
  const [selectedYear, setSelectedYear] = useState(boundedInitialDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(boundedInitialDate.getMonth());
  const [selectedDay, setSelectedDay] = useState(boundedInitialDate.getDate());

  // Handle month/year changes that might make the day invalid
  const validateDayForMonth = (month: number, year: number, day: number) => {
    const maxDaysInMonth = new Date(year, month + 1, 0).getDate();
    if (day > maxDaysInMonth) {
      return maxDaysInMonth; // Return the max valid day
    }
    return day;
  };

  const applyBoundedDate = (year: number, month: number, day: number) => {
    const validDay = validateDayForMonth(month, year, day);
    const boundedDate = clampDate(
      new Date(year, month, validDay),
      minimumDate,
      maximumDate
    );

    setSelectedYear(boundedDate.getFullYear());
    setSelectedMonth(boundedDate.getMonth());
    setSelectedDay(boundedDate.getDate());
    onChange(boundedDate);
  };

  useEffect(() => {
    const boundedValue = clampDate(value, minimumDate, maximumDate);
    setSelectedYear(boundedValue.getFullYear());
    setSelectedMonth(boundedValue.getMonth());
    setSelectedDay(boundedValue.getDate());
  }, [value, minimumDate, maximumDate]);

  // Generate arrays for picker values
  const minYear = minimumDate ? minimumDate.getFullYear() : today.getFullYear() - 10;
  const maxYear = maximumDate ? maximumDate.getFullYear() : today.getFullYear() + 10;
  const years = Array.from(
    { length: Math.max(maxYear - minYear + 1, 1) },
    (_, i) => minYear + i
  );
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const isMonthSelectable = (monthIndex: number, year: number): boolean => {
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0);

    if (minimumDate && monthEnd < normalizeDate(minimumDate)) {
      return false;
    }

    if (maximumDate && monthStart > normalizeDate(maximumDate)) {
      return false;
    }

    return true;
  };

  // Get max days in the selected month
  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(day => {
    const candidateDate = new Date(selectedYear, selectedMonth, day);
    if (minimumDate && candidateDate < normalizeDate(minimumDate)) return false;
    if (maximumDate && candidateDate > normalizeDate(maximumDate)) return false;
    return true;
  });

  const monthItems = months
    .map((label, index) => ({ label, index }))
    .filter(item => isMonthSelectable(item.index, selectedYear));

  const isDateInHighlightedRange = (date: Date): boolean => {
    if (!highlightedRange) return false;
    const cellDate = normalizeDate(date);
    const start = normalizeDate(highlightedRange.start);
    const end = normalizeDate(highlightedRange.end);
    return cellDate >= start && cellDate <= end;
  };

  // Custom picker row component
  const PickerItem = ({
    label,
    selected,
    inRange = false,
  }: {
    label: string | number;
    selected: boolean;
    inRange?: boolean;
  }) => (
    <View
      style={[
        pickerStyles.pickerItem,
        selected && pickerStyles.selectedItem,
        selected && dkSelected,
      ]}
    >
      <Text
        style={[
          pickerStyles.pickerItemText,
          inRange && pickerStyles.rangeItemText,
          selected && pickerStyles.selectedItemText,
        ]}
      >
        {label}
      </Text>
    </View>
  );

  const DayItem = ({
    day,
    selected,
    inRange,
  }: {
    day: number;
    selected: boolean;
    inRange: boolean;
  }) => (
    <View style={pickerStyles.pickerItem}>
      <View
        style={[
          pickerStyles.dayCircle,
          inRange && !selected && pickerStyles.dayCircleInRange,
          inRange && !selected && dkSelected,
          selected && pickerStyles.dayCircleSelected,
        ]}
      >
        <Text
          style={[
            pickerStyles.pickerItemText,
            inRange && !selected && pickerStyles.rangeItemText,
            selected && pickerStyles.selectedDayText,
          ]}
        >
          {day}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={[pickerStyles.container, dkContainer]}>
      <View style={pickerStyles.pickerColumn}>
        <Text style={pickerStyles.pickerLabel}>Month</Text>
        <FlatList
          data={monthItems}
          renderItem={({ item }) => (
            <TouchableOpacity
              onPress={() => {
                applyBoundedDate(selectedYear, item.index, selectedDay);
              }}
              style={pickerStyles.itemTouchable}
            >
              <PickerItem label={item.label} selected={item.index === selectedMonth} />
            </TouchableOpacity>
          )}
          keyExtractor={(item) => item.index.toString()}
          showsVerticalScrollIndicator={true}
          initialScrollIndex={Math.max(0, monthItems.findIndex(item => item.index === selectedMonth))}
          getItemLayout={(data, index) => ({
            length: 44,
            offset: 44 * index,
            index,
          })}
          style={pickerStyles.pickerList}
        />
      </View>

      <View style={pickerStyles.pickerColumn}>
        <Text style={pickerStyles.pickerLabel}>Day</Text>
        <FlatList
          data={days}
          renderItem={({ item }) => (
            <TouchableOpacity
              onPress={() => {
                applyBoundedDate(selectedYear, selectedMonth, item);
              }}
              style={pickerStyles.itemTouchable}
            >
              <DayItem
                day={item}
                selected={item === selectedDay}
                inRange={isDateInHighlightedRange(new Date(selectedYear, selectedMonth, item))}
              />
            </TouchableOpacity>
          )}
          keyExtractor={(item) => item.toString()}
          showsVerticalScrollIndicator={true}
          initialScrollIndex={selectedDay - 1}
          getItemLayout={(data, index) => ({
            length: 44,
            offset: 44 * index,
            index,
          })}
          style={pickerStyles.pickerList}
        />
      </View>

      <View style={pickerStyles.pickerColumn}>
        <Text style={pickerStyles.pickerLabel}>Year</Text>
        <FlatList
          data={years}
          renderItem={({ item }) => (
            <TouchableOpacity
              onPress={() => {
                applyBoundedDate(item, selectedMonth, selectedDay);
              }}
              style={pickerStyles.itemTouchable}
            >
              <PickerItem label={item} selected={item === selectedYear} />
            </TouchableOpacity>
          )}
          keyExtractor={(item) => item.toString()}
          showsVerticalScrollIndicator={true}
          initialScrollIndex={years.indexOf(selectedYear)}
          getItemLayout={(data, index) => ({
            length: 44,
            offset: 44 * index,
            index,
          })}
          style={pickerStyles.pickerList}
        />
      </View>
    </View>
  );
};

const MobileDatePicker: React.FC<MobileDatePickerProps> = ({
  value,
  onChange,
  placeholder = "Select Date",
  minimumDate,
  maximumDate,
  displayText,
  highlightedRange,
  error = false,
  disabled = false,
  iconColor = "#333333",
  disabledIconColor = "#999",
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [showPicker, setShowPicker] = useState(false);
  const [tempDate, setTempDate] = useState<Date>(
    clampDate(value || new Date(), minimumDate, maximumDate)
  );

  // Update tempDate when value changes externally
  useEffect(() => {
    const nextDate = value || new Date();
    setTempDate(clampDate(nextDate, minimumDate, maximumDate));
  }, [value, minimumDate, maximumDate]);

  const handleConfirm = () => {
    onChange(clampDate(tempDate, minimumDate, maximumDate));
    setShowPicker(false);
  };

  const handleCancel = () => {
    setShowPicker(false);
    setTempDate(clampDate(value || new Date(), minimumDate, maximumDate));
  };

  const handleChange = (event: any, selectedDate?: Date) => {
    if (!selectedDate) {
      return;
    }

    const boundedDate = clampDate(selectedDate, minimumDate, maximumDate);
    setTempDate(boundedDate);

    if (Platform.OS === "android") {
      setShowPicker(false);
      onChange(boundedDate);
    }
  };

  const handleCustomPickerChange = (date: Date) => {
    setTempDate(clampDate(date, minimumDate, maximumDate));
  };

  return (
    <View>
      <TouchableOpacity
        onPress={() => {
          if (disabled) return;
          setTempDate(clampDate(value || new Date(), minimumDate, maximumDate));
          setShowPicker(true);
        }}
        style={[
          styles.pickerButton,
          error && styles.errorBorder,
          disabled && styles.disabled,
          isDark
            ? {
                backgroundColor: colors.background.elevated,
                borderColor: colors.border.default,
              }
            : {
                backgroundColor: "white",
                borderColor: "#ced4da",
              },
        ]}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <Text
          style={[
            styles.dateText,
            !value && !displayText && styles.placeholder,
            disabled && styles.disabledText,
            isDark
              ? { color: colors.text.primary }
              : { color: "#333" },
            isDark && !value && !displayText && { color: colors.text.disabled },
            !isDark && !value && !displayText && { color: "#999" },
          ]}
        >
          {displayText || (value ? formatDate(value) : placeholder)}
        </Text>
        <MaterialIcons
          name="calendar-today"
          size={20}
          color={disabled
            ? (isDark ? colors.text.disabled : disabledIconColor)
            : (isDark ? colors.text.secondary : iconColor)}
        />
      </TouchableOpacity>

      {Platform.OS === "ios" && showPicker && (
        <Modal
          animationType="slide"
          transparent
          visible={showPicker}
          presentationStyle="overFullScreen"
        >
          <SafeAreaView style={styles.modalOverlay}>
            <View style={[styles.modalContent, isDark && { backgroundColor: colors.background.elevated }]}>
              <View style={[styles.modalHeader, isDark && { borderBottomColor: colors.border.default }]}>
                <TouchableOpacity
                  onPress={handleCancel}
                  style={styles.modalButtonContainer}
                  testID="datepicker-cancel"
                >
                  <Text style={styles.modalButton}>Cancel</Text>
                </TouchableOpacity>
                <Text style={styles.modalTitle}>Select Date</Text>
                <TouchableOpacity
                  onPress={handleConfirm}
                  style={styles.modalButtonContainer}
                  testID="datepicker-done"
                >
                  <Text style={styles.modalButton}>Done</Text>
                </TouchableOpacity>
              </View>

              {/* Use our custom date picker as a fallback if native one fails */}
              <View style={styles.datePickerContainer}>
                <CustomIOSDatePicker
                  value={tempDate}
                  onChange={handleCustomPickerChange}
                  minimumDate={minimumDate}
                  maximumDate={maximumDate}
                  highlightedRange={highlightedRange}
                />
              </View>
            </View>
          </SafeAreaView>
        </Modal>
      )}

      {Platform.OS === "android" && showPicker && (
        <DateTimePicker
          value={tempDate}
          mode="date"
          display="default"
          onChange={handleChange}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
        />
      )}
    </View>
  );
};

const pickerStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 20,
    backgroundColor: "#000000",
  },
  pickerColumn: {
    flex: 1,
    height: 200,
    alignItems: "center",
  },
  pickerLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
    marginBottom: 8,
    fontFamily: FontFamily.medium,
  },
  pickerList: {
    flexGrow: 0,
    height: 176,
  },
  itemTouchable: {
    width: "100%",
    alignItems: "center",
  },
  pickerItem: {
    height: 44,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 10,
    width: "100%",
  },
  selectedItem: {
    backgroundColor: "#333333",
    borderRadius: 8,
  },
  pickerItemText: {
    fontSize: 16,
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  rangeItemText: {
    color: "#FFFFFF",
    fontFamily: FontFamily.medium,
  },
  selectedItemText: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: FontFamily.medium,
  },
  dayCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  dayCircleInRange: {
    backgroundColor: "#333333",
  },
  dayCircleSelected: {
    borderColor: "#FFFFFF",
    borderWidth: 2,
  },
  selectedDayText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontFamily: FontFamily.bold,
  },
});

const styles = StyleSheet.create({
  pickerButton: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
    backgroundColor: "#202020",
    borderWidth: 1,
    borderRadius: 4,
    borderColor: "#404040",
    minHeight: 40,
  },
  dateText: { fontSize: 14, color: "#F2F2F2", fontFamily: FontFamily.regular },
  placeholder: { color: "#B0B0B0", fontFamily: FontFamily.regular },
  errorBorder: { borderColor: "#dc3545" },
  disabled: { backgroundColor: "#2B2B2B", borderColor: "#404040" },
  disabledText: { color: "#6E6E6E" },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  modalContent: {
    backgroundColor: "#000000",
    paddingBottom: Platform.OS === "ios" ? 34 : 20,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomColor: "#333333",
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
    flex: 1,
    fontFamily: FontFamily.medium,
    color: "#FFFFFF",
  },
  modalButtonContainer: {
    minWidth: 60,
    padding: 8,
  },
  modalButton: {
    fontSize: 16,
    textAlign: "center",
    fontFamily: FontFamily.medium,
    color: "#FFFFFF",
  },
  datePickerContainer: {
    width: "100%",
  },
});

export default MobileDatePicker;
